import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getLandlordInsurancePricing,
  updateLandlordInsurancePricing,
} from "../context/functions/AdminFunction";

const COVERAGES = [6, 12, 18, 24];
const FRANQUICIAS = [0];

const emptyProduct = () => ({
  standardCoverageMonths: 12,
  standardFranquiciaMonths: 0,
  rates: {},
});

const toEditablePricing = (pricing) => {
  const convertProduct = (product) => ({
    standardCoverageMonths: Number(product?.standardCoverageMonths ?? 12),
    standardFranquiciaMonths: 0,
    rates: (product?.rates || []).reduce((cells, cell) => {
      const percentage = Number((Number(cell.rate) * 100).toFixed(6));
      cells[`${cell.coverageMonths}:${cell.franquiciaMonths}`] =
        String(percentage);
      return cells;
    }, {}),
  });

  const editablePricing = {
    insurance: pricing?.insurance
      ? convertProduct(pricing.insurance)
      : emptyProduct(),
    skorProtect: pricing?.skorProtect
      ? convertProduct(pricing.skorProtect)
      : emptyProduct(),
  };

  const standardCoverageMonths =
    editablePricing.insurance.standardCoverageMonths;
  const standardFranquiciaMonths =
    editablePricing.insurance.standardFranquiciaMonths;

  editablePricing.insurance.standardCoverageMonths = standardCoverageMonths;
  editablePricing.insurance.standardFranquiciaMonths =
    standardFranquiciaMonths;
  editablePricing.skorProtect.standardCoverageMonths = standardCoverageMonths;
  editablePricing.skorProtect.standardFranquiciaMonths =
    standardFranquiciaMonths;

  return editablePricing;
};

const toApiProduct = (product) => ({
  standardCoverageMonths: Number(product.standardCoverageMonths),
  standardFranquiciaMonths: Number(product.standardFranquiciaMonths),
  rates: COVERAGES.flatMap((coverageMonths) =>
    FRANQUICIAS.map((franquiciaMonths) => {
      const value = product.rates[`${coverageMonths}:${franquiciaMonths}`];
      if (value === undefined || value === "") return null;
      return {
        coverageMonths,
        franquiciaMonths,
        rate: Number(value) / 100,
      };
    }).filter(Boolean),
  ),
});

const ProductRateTable = ({ label, productKey, value, onChange }) => {
  const updateRate = (coverageMonths, franquiciaMonths, nextValue) => {
    if (
      nextValue !== "" &&
      (Number(nextValue) <= 0 || Number(nextValue) > 100)
    ) {
      return;
    }
    onChange(productKey, {
      ...value,
      rates: {
        ...value.rates,
        [`${coverageMonths}:${franquiciaMonths}`]: nextValue,
      },
    });
  };

  return (
    <section className="insurance-pricing-product">
      <div className="mb-3">
        <h6 className="fw-bold mb-1">{label}</h6>
        <div className="text-muted small">
          Empty cells are unavailable in the landlord app.
        </div>
      </div>

      <div className="insurance-pricing-table-wrap">
        <Table bordered responsive className="insurance-pricing-table mb-0">
          <thead>
            <tr>
              <th>Coverage / Franquicia</th>
              {FRANQUICIAS.map((months) => (
                <th key={months}>{months}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COVERAGES.map((coverageMonths) => (
              <tr key={coverageMonths}>
                <th>{coverageMonths} months</th>
                {FRANQUICIAS.map((franquiciaMonths) => {
                  const key = `${coverageMonths}:${franquiciaMonths}`;
                  const isStandard =
                    coverageMonths === value.standardCoverageMonths &&
                    franquiciaMonths === value.standardFranquiciaMonths;
                  return (
                    <td key={key} className={isStandard ? "is-standard" : ""}>
                      <div className="insurance-rate-input">
                        <Form.Control
                          type="number"
                          min="0.01"
                          max="100"
                          step="0.01"
                          aria-label={`${label}, ${coverageMonths} months coverage, ${franquiciaMonths} months franquicia`}
                          value={value.rates[key] ?? ""}
                          placeholder="—"
                          onChange={(event) =>
                            updateRate(
                              coverageMonths,
                              franquiciaMonths,
                              event.target.value,
                            )
                          }
                        />
                        <span>%</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </section>
  );
};

const InsurancePricingModal = ({ show, landlord, onHide }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [applyToAllWorkspaces, setApplyToAllWorkspaces] = useState(false);
  const [pricing, setPricing] = useState(toEditablePricing());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find(
        (workspace) => String(workspace.id) === selectedWorkspaceId,
      ),
    [selectedWorkspaceId, workspaces],
  );

  const loadWorkspacePricing = (workspace) => {
    setSelectedWorkspaceId(workspace ? String(workspace.id) : "");
    setPricing(toEditablePricing(workspace?.pricing));
  };

  useEffect(() => {
    if (!show || !landlord?._id) return undefined;
    let isActive = true;
    getLandlordInsurancePricing(landlord._id)
      .then((response) => {
        if (!isActive) return;
        const nextWorkspaces = response?.success_res?.workspaces || [];
        setWorkspaces(nextWorkspaces);
        loadWorkspacePricing(nextWorkspaces[0]);
      })
      .catch((error) => {
        if (!isActive) return;
        console.log(error);
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Error while fetching insurance pricing",
        );
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [show, landlord?._id]);

  const handleProductChange = (productKey, product) => {
    setPricing((current) => ({ ...current, [productKey]: product }));
  };

  const handleStandardChange = (field, value) => {
    const nextValue = Number(value);
    setPricing((current) => ({
      insurance: {
        ...current.insurance,
        [field]: nextValue,
      },
      skorProtect: {
        ...current.skorProtect,
        [field]: nextValue,
      },
    }));
  };

  const validateStandardCells = () => {
    for (const [productKey, product] of Object.entries(pricing)) {
      const standardKey = `${product.standardCoverageMonths}:${product.standardFranquiciaMonths}`;
      if (!product.rates[standardKey]) {
        toast.error(
          `${productKey === "insurance" ? "Insurance" : "Skor Protect"} standard combination must have a rate`,
        );
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!applyToAllWorkspaces && !selectedWorkspaceId) {
      toast.error("Please select a workspace");
      return;
    }
    if (!validateStandardCells()) return;

    setSaving(true);
    try {
      await updateLandlordInsurancePricing(landlord._id, {
        workspaceId: selectedWorkspaceId,
        applyToAllWorkspaces,
        insurance: toApiProduct(pricing.insurance),
        skorProtect: toApiProduct(pricing.skorProtect),
      });
      toast.success(
        applyToAllWorkspaces
          ? "Insurance pricing applied to all workspaces"
          : "Workspace insurance pricing updated",
      );
      onHide();
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Error while saving insurance pricing",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onHide();
  };

  return (
    <Modal show={show} size="lg" centered scrollable onHide={handleClose}>
      <Modal.Header closeButton className="landlord-modal-header">
        <Modal.Title className="fw-bold h5">
          Insurance Pricing
          {landlord?.fname_companyName
            ? ` - ${landlord.fname_companyName}`
            : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="landlord-modal-body insurance-pricing-modal-body">
        {loading ? (
          <div className="insurance-pricing-loading">
            <Spinner animation="border" size="sm" /> Loading workspace
            pricing...
          </div>
        ) : workspaces.length === 0 ? (
          <div className="text-muted">This landlord has no workspaces.</div>
        ) : (
          <>
            <div className="insurance-pricing-settings mb-3">
              <div>
                <Form.Label className="landlord-form-label">
                  Workspace
                </Form.Label>
                <Form.Select
                  className="landlord-form-control"
                  value={selectedWorkspaceId}
                  onChange={(event) => {
                    const nextWorkspace = workspaces.find(
                      (workspace) =>
                        String(workspace.id) === event.target.value,
                    );
                    loadWorkspacePricing(nextWorkspace);
                  }}
                >
                  {workspaces.map((workspace) => (
                    <option key={workspace.id} value={workspace.id}>
                      {workspace.name}
                      {workspace.pricing?.isDefault ? " (using defaults)" : ""}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <div className="insurance-pricing-scope-label">
                  <Form.Label className="landlord-form-label">
                    Scope
                  </Form.Label>
                  <span>All {workspaces.length} workspaces</span>
                </div>
                <div className="insurance-apply-all-box">
                  <Form.Check
                    id="insurance-pricing-apply-all"
                    type="checkbox"
                    checked={applyToAllWorkspaces}
                    onChange={(event) =>
                      setApplyToAllWorkspaces(event.target.checked)
                    }
                    label="Apply to all workspaces"
                  />
                </div>
              </div>
              <div>
                <Form.Label className="landlord-form-label">
                  Standard coverage
                </Form.Label>
                <Form.Select
                  className="landlord-form-control"
                  value={pricing.insurance.standardCoverageMonths}
                  onChange={(event) =>
                    handleStandardChange(
                      "standardCoverageMonths",
                      event.target.value,
                    )
                  }
                >
                  {COVERAGES.map((months) => (
                    <option key={months} value={months}>
                      {months} months
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <Form.Label className="landlord-form-label">
                  Standard franquicia
                </Form.Label>
                <Form.Select
                  className="landlord-form-control"
                  value={pricing.insurance.standardFranquiciaMonths}
                  onChange={(event) =>
                    handleStandardChange(
                      "standardFranquiciaMonths",
                      event.target.value,
                    )
                  }
                >
                  {FRANQUICIAS.map((months) => (
                    <option key={months} value={months}>
                      {months} months
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            {selectedWorkspace?.pricing?.isDefault && (
              <div className="insurance-pricing-default-note mb-3">
                This workspace has no custom pricing yet. The default values are
                shown.
              </div>
            )}

            <ProductRateTable
              label="Insurance"
              productKey="insurance"
              value={pricing.insurance}
              onChange={handleProductChange}
            />
            <ProductRateTable
              label="Skor Protect"
              productKey="skorProtect"
              value={pricing.skorProtect}
              onChange={handleProductChange}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button
          variant="outline-secondary"
          className="landlord-action-btn"
          disabled={saving}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          className="landlord-btn-apply"
          disabled={loading || saving || workspaces.length === 0}
          onClick={handleSave}
        >
          {saving ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Saving...
            </>
          ) : applyToAllWorkspaces ? (
            "Save for all workspaces"
          ) : (
            "Save pricing"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InsurancePricingModal;
